/**
 * Display primitives for the light design system.
 *
 * The load-bearing one is <Unavailable/>: a metric the backend could not
 * derive is null, and this renders an explicit state with the reason. It must
 * never fall back to 0, 100%, or a blank that reads as a number.
 */
import type { ReactNode } from 'react';

export function Metric({
  label,
  value,
  unit,
  sub,
  from,
  tone = 'default',
  children,
}: {
  label: string;
  value?: ReactNode;
  unit?: string;
  sub?: ReactNode;
  /** Data-quality line: where the figure comes from, or why it is absent. */
  from?: ReactNode;
  tone?: 'default' | 'ok' | 'warn' | 'danger';
  children?: ReactNode;
}) {
  const toneClass = tone === 'default' ? '' : `tone-${tone}`;
  return (
    <div className={`card metric ${toneClass}`}>
      <div className="metric-label">{label}</div>
      {value !== undefined && value !== null ? (
        <div className="metric-value">
          {value}
          {unit ? <span className="unit"> {unit}</span> : null}
        </div>
      ) : (
        children
      )}
      {sub ? <div className="metric-sub">{sub}</div> : null}
      {from ? <div className="metric-from">{from}</div> : null}
    </div>
  );
}

/** Renders when a figure genuinely cannot be computed. */
export function Unavailable({ reason }: { reason: string }) {
  return (
    <div className="metric-unavailable">
      <span className="val">Not available</span>
      <span className="why">{reason}</span>
    </div>
  );
}

export function Card({
  title,
  note,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card rise ${className ?? ''}`}>
      {(title || actions) && (
        <div className="card-head">
          {title ? <h3>{title}</h3> : null}
          {actions}
        </div>
      )}
      {children}
      {note ? <div className="card-note">{note}</div> : null}
    </section>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      {label}
    </div>
  );
}

export function ErrorBox({ message }: { message: string }) {
  return (
    <div className="error-box" role="alert">
      {message}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

/** Percentage with one decimal, or an explicit unavailable state. */
export function Pct({ value, unavailableReason }: { value: number | null; unavailableReason?: string }) {
  if (value === null || value === undefined) {
    return <Unavailable reason={unavailableReason ?? 'Not enough labelled data.'} />;
  }
  return <>{value.toFixed(1)}%</>;
}

export function StatePill({ state }: { state: string }) {
  const map: Record<string, string> = {
    PASSIVE: 'muted',
    SHADOW: 'info',
    ACTIVE: 'ok',
    GRANTED: 'ok',
    AT_RISK: 'warn',
    DENIED: 'danger',
    NO_SUBSCRIPTION: 'muted',
    PASS: 'ok',
    FLAG_ADVISORY: 'advisory',
    REJECT_NON_SAFETY_GATE: 'warn',
    INSUFFICIENT_DATA: 'muted',
  };
  return <span className={`pill ${map[state] ?? 'muted'}`}>{state.replace(/_/g, ' ')}</span>;
}

/** Small uppercase tag for phase/recipe/kind metadata. */
export function Tag({ children, kind }: { children: ReactNode; kind?: 'brand' | 'advisory' | 'warn' }) {
  return <span className={`tag ${kind ?? ''}`}>{children}</span>;
}
